<script lang="ts">
  import { OCTI_WEB_REPO_URL } from "../../version";
  import Icon from "./Icon.svelte";
  import OverflowMenu, { type MenuItem } from "./OverflowMenu.svelte";

  /**
   * Sticky dashboard nav. Hosts:
   * - Page title + identity subtitle (signed-in label + server domain).
   * - Version pill + last-sync pill (passive at-a-glance state).
   * - Permanent Refresh button.
   * - Settings cog (opens the SettingsScreen sheet).
   * - Overflow menu (Republish / Add device / Sign-out).
   *
   * Stays above device cards (z: 100) and renders its dropdown above
   * per-card menus (z: 110 inside OverflowMenu).
   */
  let {
    accountSubtitle,
    version,
    lastSyncLabel,
    loading,
    issuesCount = 0,
    onRefresh,
    onOpenIssues,
    onOpenSettings,
    menuItems,
  }: {
    accountSubtitle: string;
    version: string;
    lastSyncLabel: string;
    loading: boolean;
    /** Number of active per-connector issues. When > 0 the bell button shows
     *  a red dot + count; clicking opens {@link onOpenIssues}. */
    issuesCount?: number;
    onRefresh: () => void;
    /** Optional — when provided AND `issuesCount > 0` the bell button renders. */
    onOpenIssues?: () => void;
    onOpenSettings: () => void;
    menuItems: MenuItem[];
  } = $props();

  const releaseUrl = $derived(`${OCTI_WEB_REPO_URL}/releases/tag/v${version}`);
</script>

<header class="nav">
  <div class="identity">
    <!--
      BASE_URL resolves to "/" on stable, "/canary/" on the canary channel
      build, so the icon URL stays correct when the SPA is served from a
      subpath.
    -->
    <img
      class="brand-icon"
      src="{import.meta.env.BASE_URL}icons/octi-192.png"
      width="36"
      height="36"
      alt=""
    />
    <div class="identity-text">
      <h1 class="title">Octi Web</h1>
      <div class="subtitle">{accountSubtitle}</div>
    </div>
  </div>
  <div class="pills">
    <a
      class="pill pill-link"
      href={releaseUrl}
      target="_blank"
      rel="noopener noreferrer"
      title="View release notes on GitHub"
    >
      v{version}
    </a>
    <span class="pill last-sync" title="Last successful sync">
      {loading ? "Syncing…" : `Sync: ${lastSyncLabel}`}
    </span>
  </div>
  <div class="actions">
    <button
      type="button"
      class="refresh"
      aria-label="Refresh now"
      onclick={onRefresh}
      disabled={loading}
    >
      <Icon name="refresh" size={16} />
      <span class="refresh-label">Refresh</span>
    </button>
    {#if issuesCount > 0 && onOpenIssues}
      <button
        type="button"
        class="icon-btn issues-btn"
        aria-label="{issuesCount} active issue{issuesCount === 1 ? '' : 's'}"
        data-testid="nav-issues"
        onclick={onOpenIssues}
      >
        <Icon name="alert" size={18} />
        <span class="issues-badge">{issuesCount > 9 ? "9+" : issuesCount}</span>
      </button>
    {/if}
    <button
      type="button"
      class="icon-btn"
      aria-label="Settings"
      data-testid="nav-settings"
      onclick={onOpenSettings}
    >
      <Icon name="settings" size={18} />
    </button>
    <OverflowMenu items={menuItems} ariaLabel="Account actions" />
  </div>
</header>

<style>
  .nav {
    display: grid;
    /* mobile-first: stack identity over pills/actions */
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "identity actions"
      "pills    pills";
    gap: 8px 12px;
    align-items: center;
    padding: 12px 16px;
    margin-bottom: 16px;
    border-radius: 12px;
    background: color-mix(in srgb, var(--md-color-surface-container) 92%, transparent);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid var(--md-color-outline-variant);
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .identity {
    grid-area: identity;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .brand-icon {
    flex-shrink: 0;
    border-radius: 8px;
    display: block;
  }
  .identity-text {
    min-width: 0;
  }
  .title {
    font-size: 1.3rem;
    font-weight: 600;
    margin: 0;
    line-height: 1.15;
    color: var(--md-color-on-surface);
  }
  .subtitle {
    font-size: 0.78rem;
    color: var(--md-color-on-surface-variant);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pills {
    grid-area: pills;
    display: inline-flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }
  .pill {
    font-size: 0.72rem;
    color: var(--md-color-on-surface-variant);
    background: color-mix(in srgb, var(--md-color-on-surface) 6%, transparent);
    padding: 3px 8px;
    border-radius: 999px;
    line-height: 1.2;
    white-space: nowrap;
  }
  .pill-link {
    text-decoration: none;
    cursor: pointer;
  }
  .pill-link:hover,
  .pill-link:focus-visible {
    color: var(--md-color-on-surface);
    background: color-mix(in srgb, var(--md-color-on-surface) 12%, transparent);
  }

  .actions {
    grid-area: actions;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .refresh {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 18px;
    border: 1px solid var(--md-color-outline-variant);
    background: transparent;
    color: var(--md-color-on-surface);
    font-size: 0.82rem;
    cursor: pointer;
  }
  .refresh:hover:not(:disabled) {
    background: color-mix(in srgb, var(--md-color-on-surface) 8%, transparent);
  }
  .refresh:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .refresh-label {
    /* Hidden on narrow viewports — icon stays. */
    display: none;
  }
  .icon-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: var(--md-color-on-surface-variant);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
  .icon-btn:hover {
    background: color-mix(in srgb, var(--md-color-on-surface) 8%, transparent);
    color: var(--md-color-on-surface);
  }
  .issues-btn {
    position: relative;
    color: var(--md-color-error);
  }
  .issues-btn:hover {
    color: var(--md-color-error);
    background: color-mix(in srgb, var(--md-color-error) 12%, transparent);
  }
  .issues-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: var(--md-color-error);
    color: var(--md-color-on-error, white);
    font-size: 0.62rem;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
    box-sizing: border-box;
  }

  /* Wider viewports: identity + pills inline, actions on the right */
  @media (min-width: 720px) {
    .nav {
      grid-template-columns: 1fr auto auto;
      grid-template-areas: "identity pills actions";
    }
    .refresh-label {
      display: inline;
    }
  }
</style>
